import React, { useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useForm } from 'react-hook-form'
import { SignUpReq } from '@/services/auth/login.types'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { useSignUp } from '@/services/auth/useAuthQueries'
import ShipperSelectModal from '@/components/system/ShipperSelectModal'
import DepartmentSelectModal from '@/components/system/DepartmentSelectModal'
import { PATHS } from '@/utils/routes/paths'

// Figma 25:13775 — MOMS Okta 회원가입 페이지
const AGREEMENTS = [
  { key: 'IS_PRIVACY_POLICY' as const, label: '회원가입 수집 고지에 동의합니다.', link: 'https://www.musinsalogistics.co.kr/privacy/moms/moms_privacy_user.html' },
  { key: 'IS_SEC_TRAIN' as const, label: '파트너 개인정보 보호 교육 이수에 동의합니다.', link: 'https://www.musinsalogistics.co.kr/privacy/moms/moms_privacy_partner.pdf' },
  { key: 'IS_SEC_AGREE' as const, label: '외부자 정보보안 서약서에 동의합니다.', link: 'https://www.musinsalogistics.co.kr/privacy/moms/moms_privacy_external.html' },
]

// LOGISPRD-9412: 마침표(.)는 로컬/도메인 모두 맨앞·맨뒤에 올 수 없고 연속 2개 이상 불가.
const EMAIL_REGEX = /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/
const PHONE_REGEX = /^01[0-9]{8,9}$/

interface FormErrors {
  email?: string
  nickname?: string
  shipper?: string
  dept?: string
  phone?: string
  agreement?: string
}

// LOGISPRD-8531: Okta 회원가입 — Figma 25:13775 MOMS Okta 회원가입.
function OktaSignUp() {
  const navigate = useNavigate()
  const signup = useSignUp()

  const form = useForm<SignUpReq>({
    defaultValues: {
      email: '',
      password: '',
      password2: '',
      nickname: '',
      SPR_NM: '',
      shipper_name: '',
      shipper_code: '',
      DEPT_NM: '',
      phone: '',
      IS_SEC_AGREE: false,
      IS_PRIVACY_POLICY: false,
      IS_SEC_TRAIN: false,
    },
  })
  const values = form.watch()

  const [errors, setErrors] = React.useState<FormErrors>({})

  const setField = useCallback(
    (name: keyof SignUpReq, v: string | boolean) => {
      form.setValue(name, v as never)
    },
    [form],
  )

  const validate = useCallback((): boolean => {
    const f = form.getValues()
    const e: FormErrors = {}

    if (!f.nickname) e.nickname = '이름을 입력해주세요'

    if (!f.email) e.email = '이메일을 입력해주세요'
    else if (!EMAIL_REGEX.test(f.email)) e.email = '유효한 이메일을 입력하세요'

    if (!f.SPR_NM) e.shipper = '소속을 선택해주세요'
    if (!f.DEPT_NM) e.dept = '부서를 선택해주세요'

    if (!f.phone) e.phone = '휴대폰번호를 입력해주세요'
    else if (!PHONE_REGEX.test(f.phone)) e.phone = '유효한 휴대폰번호를 입력하세요'

    if (!f.IS_PRIVACY_POLICY || !f.IS_SEC_TRAIN || !f.IS_SEC_AGREE) e.agreement = '모든 필수 동의 항목에 동의해주세요'

    setErrors(e)
    return Object.keys(e).length === 0
  }, [form])

  const allAgreed = values.IS_PRIVACY_POLICY && values.IS_SEC_TRAIN && values.IS_SEC_AGREE
  // Spec: docs/spec/auth/SPEC-auth-signup-public-api-reduction-A.yaml#signup-submits-with-valid-format-only
  const canSubmit =
    !!values.nickname && !!values.email && !!values.SPR_NM && !!values.DEPT_NM && !!values.phone && allAgreed

  const onSubmit = useCallback(() => {
    if (!validate()) return
    signup
      .mutateAsync(form.getValues())
      .then(() => navigate(PATHS['auth.okta-signup-complete']))
      .catch((e) => MAlert.show(Utils.getApiErrMessage(e, '회원가입을 할 수 없습니다.')))
  }, [validate, form, signup, navigate])

  return createPortal(
    <Page>
      <Inner>
      {/* title */}
      <TitleBlock>
        <Logo>MOMS</Logo>
        <Title>회원가입</Title>
        <Subtitle>계정 정보를 입력하세요</Subtitle>
      </TitleBlock>

      <Divider />

      {/* textfield 영역 */}
      <Fields>
        <Field
          label="이름"
          required
          placeholder="이름을 입력하세요"
          value={values.nickname}
          error={errors.nickname}
          clearable
          maxLength={30}
          onChange={(v) => { setField('nickname', v); setErrors((e) => ({ ...e, nickname: undefined })) }}
        />

        <Field
          label="이메일"
          required
          placeholder="musinsa@musinsa.com"
          value={values.email}
          error={errors.email}
          clearable
          onChange={(v) => { setField('email', v); setErrors((e) => ({ ...e, email: undefined })) }}
        />

        <Row>
          <Field label="소속 (화주)" required placeholder="소속 선택" value={values.shipper_name} error={errors.shipper} disabled />
          <ShipperSelectModal
            title="소속 찾기"
            shipperName={values.shipper_name}
            trigger={<SideButton>찾기</SideButton>}
            onSelect={(v) => {
              setField('SPR_NM', v.shipper_code)
              setField('shipper_code', v.shipper_code)
              setField('shipper_name', v.shipper_name)
              setField('DEPT_NM', '')
              setErrors((e) => ({ ...e, shipper: undefined, dept: undefined }))
            }}
          />
        </Row>

        <Row>
          <Field label="부서" required placeholder="부서 선택" value={values.DEPT_NM} error={errors.dept} disabled />
          {values.shipper_code ? (
            <DepartmentSelectModal
              shipperCode={values.shipper_code}
              shipperName={values.shipper_name}
              trigger={<SideButton>찾기</SideButton>}
              onSelect={(v) => {
                setField('DEPT_NM', v.DEPT_NM)
                setErrors((e) => ({ ...e, dept: undefined }))
              }}
            />
          ) : (
            <SideButton disabled>찾기</SideButton>
          )}
        </Row>

        <Field
          label="휴대폰번호"
          required
          placeholder="- 제외하고 입력"
          value={values.phone}
          error={errors.phone}
          clearable
          onChange={(v) => { setField('phone', v.replace(/[^0-9]/g, '')); setErrors((e) => ({ ...e, phone: undefined })) }}
        />
      </Fields>

      <Divider />

      {/* 정보보안 동의 */}
      <AgreeBlock>
        <FieldLabel>정보보안 동의</FieldLabel>
        {errors.agreement && <HelperText>{errors.agreement}</HelperText>}
        {AGREEMENTS.map((a) => (
          <AgreeRow key={a.key}>
            <AgreeControl onClick={() => {
              const next = !values[a.key]
              if (next) window.open(a.link) // 동의 체크 시 약관을 새 탭으로 오픈
              setField(a.key, next)
              setErrors((e) => ({ ...e, agreement: undefined }))
            }}>
              <CheckboxBox $checked={!!values[a.key]}>
                {values[a.key] && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12.5l5 5 9-10" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </CheckboxBox>
              <RequiredTag>필수</RequiredTag>
              <AgreeLabel>{a.label}</AgreeLabel>
            </AgreeControl>
            <ViewButton type="button" onClick={() => window.open(a.link)}>보기</ViewButton>
          </AgreeRow>
        ))}
      </AgreeBlock>

      {/* 제출 */}
      <SubmitButton type="button" disabled={!canSubmit} onClick={onSubmit}>
        회원가입
      </SubmitButton>

      {/* footer */}
      <Footer>
        <FooterText>이미 계정이 있으신가요?</FooterText>
        <LoginLink type="button" onClick={() => navigate(PATHS['auth.login'])}>로그인</LoginLink>
      </Footer>
      </Inner>
    </Page>,
    document.body,
  )
}

/* ───────────── Field (Figma textField) ───────────── */

interface FieldProps {
  label: string
  required?: boolean
  type?: 'text' | 'password'
  placeholder?: string
  value: string
  error?: string
  disabled?: boolean
  clearable?: boolean
  maxLength?: number
  onChange?: (v: string) => void
}

function Field({ label, required, type = 'text', placeholder, value, error, disabled, clearable, maxLength, onChange }: FieldProps) {
  return (
    <FieldWrap>
      <FieldLabelRow>
        <FieldLabel>{label}</FieldLabel>
        {required && <RequiredDot />}
      </FieldLabelRow>
      <InputBox $error={!!error} $disabled={!!disabled}>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          onChange={(e) => onChange?.(e.target.value)}
        />
        {clearable && !disabled && value ? (
          <ClearButton type="button" tabIndex={-1} onClick={() => onChange?.('')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" fill="#c4c4c4" />
              <path d="M9 9l6 6M15 9l-6 6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </ClearButton>
        ) : null}
      </InputBox>
      {error ? <HelperText>{error}</HelperText> : null}
    </FieldWrap>
  )
}

/* ───────────── styled (Figma 정확값) ───────────── */

// Figma 15:32526 — 루트 bg-[var(--bg, white)] = 흰 배경 전체 (auth 레이아웃의 login_bg 이미지를 덮음)
const Page = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #fff;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

// 폼 컬럼: Figma 폼은 상단 80px, 가로 중앙, width 400
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 400px;
  max-width: calc(100% - 32px);
  padding: 80px 0 40px;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 271px;
`

const Logo = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #000;
  height: 24px;
  line-height: 24px;
`

const Title = styled.p`
  margin: 0;
  font-size: 30px;
  font-weight: 500;
  line-height: 1.4;
  color: #000;
  text-align: center;
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.36;
  color: #8a8a8a;
  text-align: center;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #ebebeb;
`

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  width: 100%;
`

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  flex: 1 0 0;
  min-width: 80px;
`

const FieldLabelRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const FieldLabel = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.36;
  color: #000;
`

const RequiredDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  /* layerDesktop/fg-accent (blue-500). 이 앱은 data-layer-platform="desktop" 라 web 스코프 토큰이
     매칭 안 돼 primitive blue-500(#245eff) 폴백으로 파란색 보장. */
  background: var(--web-fg-accent, var(--primitive-color-blue-500, #245eff));
`

const InputBox = styled.div<{ $error?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;

  input {
    flex: 1 0 0;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 15px;
    line-height: 1.36;
    color: #000;

    &::placeholder {
      color: #8a8a8a;
    }
  }

  ${({ $error }) =>
    $error &&
    css`
      border-color: #f31110;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background: rgba(138, 138, 138, 0.1);
      border-color: #ebebeb;
      input::placeholder {
        color: #8a8a8a;
      }
    `}
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
`

const HelperText = styled.p`
  margin: 0;
  padding: 2px 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #f31110;
  width: 100%;
`

const SideButton = styled.button`
  flex-shrink: 0;
  width: 84px;
  height: 44px;
  min-height: 44px;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  color: #000;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.36;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: rgba(138, 138, 138, 0.1);
    border-color: #ebebeb;
    color: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
  }
`

const AgreeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
`

const AgreeRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const AgreeControl = styled.div`
  display: flex;
  flex: 1 0 0;
  gap: 8px;
  align-items: center;
  min-width: 0;
  cursor: pointer;
`

const CheckboxBox = styled.div<{ $checked?: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid ${({ $checked }) => ($checked ? '#000' : '#e0e0e0')};
  background: ${({ $checked }) => ($checked ? '#000' : '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
`

const RequiredTag = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #ffecec;
  color: #f31110;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
`

const AgreeLabel = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #000;
  white-space: nowrap;
`

const ViewButton = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #666;
  text-decoration: underline;
`

const SubmitButton = styled.button`
  width: 100%;
  height: 52px;
  min-height: 52px;
  padding: 16px 20px;
  border: none;
  border-radius: 4px;
  background: #000;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.36;
  cursor: pointer;

  &:disabled {
    background: rgba(138, 138, 138, 0.1);
    color: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const FooterText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #8a8a8a;
`

const LoginLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #245eff;
`

export default OktaSignUp
